<script setup lang="ts">
import type { ColDef, ValueFormatterParams } from "ag-grid-community";
import { computed } from "vue";
import AppGrid from "../../components/AppGrid.vue";
import { FITTABLE_DEFAULT_COL_DEF } from "../../components/composables/useGridColumnDefaults.ts";
import StatusBanner from "../../components/StatusBanner.vue";
import type {
  BulkOrderAllocationRow,
  IntermediateOrderDiagnosticRow,
  IntermediateOrderSearchDiagnostics,
} from "../../redistribution/models/IntermediateOrderSearchResult.ts";
import {
  formatCalendarDateForDisplay,
  formatUsDate,
} from "../../utils/dateFormatting.ts";

const props = withDefaults(
  defineProps<{
    diagnostics?: IntermediateOrderSearchDiagnostics | undefined;
    allocations: BulkOrderAllocationRow[];
    showFirstCost?: boolean;
  }>(),
  { diagnostics: undefined, showFirstCost: true }
);

const FAILURE_STAGE_LABELS: Record<string, string> = {
  NotFound: "No Bulk Order or Intermediate Order matched",
  NoIntermediateOrderHeaders: "No Intermediate Order headers",
  HeadersExcludedBySearchView: "Intermediate Orders excluded by search filters",
  NoLineReferences: "Intermediate Orders have no line references",
  LineDetailMissing: "Intermediate Order line detail missing",
  LinesExcludedByProductOrDcFilters:
    "Intermediate Order lines excluded by product/DC filters",
  Undetermined: "Cause undetermined",
  DiagnosticsUnavailable: "Diagnostics unavailable",
};

const EMPTY = "—";
const ROW_HEIGHT = 36;

const stageLabel = computed(() => {
  const stage = props.diagnostics?.failureStage ?? "";
  return FAILURE_STAGE_LABELS[stage] ?? stage;
});

const integerFormat = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});
const cubeFormat = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const unitCubeFormat = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 3,
});
const currencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

function numberFormatter(format: Intl.NumberFormat) {
  return (params: ValueFormatterParams): string =>
    typeof params.value === "number" ? format.format(params.value) : EMPTY;
}

function textFormatter(params: ValueFormatterParams): string {
  return typeof params.value === "string" && params.value.trim() !== ""
    ? params.value
    : EMPTY;
}

function calendarDateFormatter(params: ValueFormatterParams): string {
  return typeof params.value === "string" && params.value !== ""
    ? formatCalendarDateForDisplay(params.value.slice(0, 10))
    : EMPTY;
}

function dateTimeFormatter(params: ValueFormatterParams): string {
  return typeof params.value === "string" && params.value !== ""
    ? formatUsDate(params.value)
    : EMPTY;
}

const intermediateOrders = computed(
  () => props.diagnostics?.intermediateOrders ?? []
);

function firstNonEmpty(...values: (string | null | undefined)[]): string {
  return (
    values.find(
      (value) => value !== null && value !== undefined && value.trim() !== ""
    ) ?? ""
  );
}

const bulkOrderNumber = computed(() =>
  firstNonEmpty(
    props.allocations[0]?.bulkOrderNumber,
    props.diagnostics?.searchTerm
  )
);

const sortedAllocations = computed(() =>
  props.allocations.toSorted(
    (a, b) =>
      a.warehouseName.localeCompare(b.warehouseName) ||
      a.rowNumber - b.rowNumber
  )
);

const warehouseSummaries = computed(() => {
  const summaries = new Map<
    string,
    { key: string; label: string; lines: number; units: number; cubes: number }
  >();
  for (const row of props.allocations) {
    const key = row.allocationWarehouseId;
    const summary = summaries.get(key) ?? {
      key,
      label: row.warehouseName || key,
      lines: 0,
      units: 0,
      cubes: 0,
    };
    summary.lines += 1;
    summary.units += row.totalUnits;
    summary.cubes += row.totalCubicFeet ?? 0;
    summaries.set(key, summary);
  }
  return [...summaries.values()].toSorted((a, b) =>
    a.label.localeCompare(b.label)
  );
});

const missingPriceCount = computed(
  () =>
    props.allocations.filter(
      (row) => row.firstCostUsd === null || row.firstCostUsd === undefined
    ).length
);

const defaultColDef: ColDef = FITTABLE_DEFAULT_COL_DEF;

const diagnosticColumnDefs: ColDef<IntermediateOrderDiagnosticRow>[] = [
  {
    field: "intermediatePoNumber",
    headerName: "IO Number",
    minWidth: 110,
    filter: "agTextColumnFilter",
  },
  {
    field: "division",
    headerName: "Div",
    minWidth: 60,
    filter: "agTextColumnFilter",
    valueFormatter: textFormatter,
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 70,
    filter: "agTextColumnFilter",
    valueFormatter: textFormatter,
  },
  {
    field: "warehouseNumber",
    headerName: "Warehouse",
    minWidth: 90,
    type: "numericColumn",
    filter: "agNumberColumnFilter",
    valueFormatter: numberFormatter(integerFormat),
  },
  {
    field: "purchaseOrderState",
    headerName: "State",
    minWidth: 100,
    filter: "agTextColumnFilter",
    valueFormatter: textFormatter,
  },
  {
    colId: "inSearchView",
    headerName: "In Search View",
    minWidth: 90,
    filter: "agTextColumnFilter",
    valueGetter: (params) => (params.data?.inSearchView ? "Yes" : "No"),
  },
  {
    field: "lineReferenceCount",
    headerName: "Line Refs",
    minWidth: 80,
    type: "numericColumn",
    filter: "agNumberColumnFilter",
  },
  {
    field: "lineDetailCount",
    headerName: "Line Detail",
    minWidth: 80,
    type: "numericColumn",
    filter: "agNumberColumnFilter",
  },
  {
    field: "searchableLineCount",
    headerName: "Searchable Lines",
    minWidth: 90,
    type: "numericColumn",
    filter: "agNumberColumnFilter",
  },
  {
    field: "lastSyncDateTime",
    headerName: "Last Sync",
    minWidth: 100,
    filter: "agTextColumnFilter",
    valueFormatter: dateTimeFormatter,
  },
];

const allAllocationColumnDefs: ColDef<BulkOrderAllocationRow>[] = [
  {
    colId: "warehouse",
    headerName: "Warehouse",
    minWidth: 95,
    filter: "agTextColumnFilter",
    valueGetter: (params) =>
      firstNonEmpty(
        params.data?.warehouseName,
        params.data?.allocationWarehouseId
      ),
  },
  {
    field: "division",
    headerName: "Div",
    minWidth: 60,
    filter: "agTextColumnFilter",
    valueFormatter: textFormatter,
  },
  {
    field: "skuNumber",
    headerName: "SKU",
    minWidth: 95,
    filter: "agTextColumnFilter",
  },
  {
    field: "productDescription",
    headerName: "Description",
    minWidth: 180,
    flex: 2,
    filter: "agTextColumnFilter",
    valueFormatter: textFormatter,
  },
  {
    field: "collection",
    headerName: "Collection",
    minWidth: 110,
    filter: "agTextColumnFilter",
    valueFormatter: textFormatter,
  },
  {
    field: "color",
    headerName: "Color",
    minWidth: 85,
    filter: "agTextColumnFilter",
    valueFormatter: textFormatter,
  },
  {
    field: "totalUnits",
    headerName: "Units",
    minWidth: 70,
    type: "numericColumn",
    filter: "agNumberColumnFilter",
    valueFormatter: numberFormatter(integerFormat),
  },
  {
    field: "unitCube",
    headerName: "Unit Cube",
    minWidth: 80,
    type: "numericColumn",
    filter: "agNumberColumnFilter",
    valueFormatter: numberFormatter(unitCubeFormat),
  },
  {
    field: "totalCubicFeet",
    headerName: "Total Cubic Feet",
    minWidth: 95,
    type: "numericColumn",
    filter: "agNumberColumnFilter",
    valueFormatter: numberFormatter(cubeFormat),
  },
  {
    field: "firstCostUsd",
    headerName: "FirstCost (USD)",
    minWidth: 95,
    type: "numericColumn",
    filter: "agNumberColumnFilter",
    valueFormatter: numberFormatter(currencyFormat),
  },
  {
    field: "shipDateRangeStart",
    headerName: "Ship Start",
    minWidth: 95,
    filter: "agTextColumnFilter",
    valueFormatter: calendarDateFormatter,
  },
  {
    field: "shipDateRangeEnd",
    headerName: "Ship End",
    minWidth: 95,
    filter: "agTextColumnFilter",
    valueFormatter: calendarDateFormatter,
  },
  {
    field: "allocationStatus",
    headerName: "Allocation Status",
    minWidth: 90,
    filter: "agTextColumnFilter",
    valueFormatter: textFormatter,
  },
];

const allocationColumnDefs = computed<ColDef<BulkOrderAllocationRow>[]>(() =>
  props.showFirstCost
    ? allAllocationColumnDefs
    : allAllocationColumnDefs.filter(
        (column) => column.field !== "firstCostUsd"
      )
);

const noSelection = {
  mode: "singleRow" as const,
  checkboxes: false,
  enableClickSelection: false,
};

function getDiagnosticRowId(parameters: { data: unknown }): string {
  const row = parameters.data as IntermediateOrderDiagnosticRow;
  return row.purchaseOrderKey || row.intermediatePoNumber;
}

function getAllocationRowId(parameters: { data: unknown }): string {
  const row = parameters.data as BulkOrderAllocationRow;
  return `${row.allocationWarehouseId}:${String(row.rowNumber)}:${row.pmKey}`;
}

function getRowHeight(): number {
  return ROW_HEIGHT;
}
</script>

<template>
  <section
    class="panel-card results-panel-card bulk-allocation-review flex min-w-0 flex-col"
    aria-label="Intermediate Order search diagnostics"
  >
    <div class="panel-card-header results-panel-header !py-2.5">
      <h2 class="m-0">
        🧾 Bulk Allocation
        <span v-if="bulkOrderNumber" class="font-medium text-slate-500"
          >— {{ bulkOrderNumber }}</span
        >
      </h2>
    </div>

    <div
      v-if="intermediateOrders.length > 0 || allocations.length > 0"
      class="results-panel-meta-strip"
    >
      <div class="results-panel-meta-left">
        <span
          v-if="intermediateOrders.length > 0"
          class="results-panel-section-chip results-panel-section-chip--primary"
        >
          Intermediate Orders: {{ intermediateOrders.length }}
        </span>
        <span
          v-if="allocations.length > 0"
          class="results-panel-section-chip results-panel-section-chip--primary"
        >
          Allocation Lines: {{ allocations.length }}
        </span>
        <span
          v-for="summary in warehouseSummaries"
          :key="summary.key"
          class="results-panel-section-chip"
          data-testid="bulk-allocation-warehouse-summary"
        >
          {{ summary.label }}: {{ summary.lines }} lines ·
          {{ integerFormat.format(summary.units) }} units ·
          {{ cubeFormat.format(summary.cubes) }} cubes
        </span>
      </div>
    </div>

    <div class="bulk-allocation-review-body">
      <StatusBanner v-if="diagnostics" variant="warning" role="alert">
        <div class="flex flex-col gap-1">
          <strong data-testid="io-search-failure-stage">{{
            stageLabel
          }}</strong>
          <span data-testid="io-search-failure-message">{{
            diagnostics.message
          }}</span>
        </div>
      </StatusBanner>

      <div
        v-if="intermediateOrders.length > 0"
        class="bulk-allocation-review-grid"
        aria-label="Intermediate Order diagnostics"
      >
        <h3 class="bulk-allocation-review-title">
          Intermediate Order Diagnostics
        </h3>
        <AppGrid
          id="intermediate-order-diagnostics-grid"
          :column-defs="diagnosticColumnDefs"
          :row-data="intermediateOrders"
          :default-col-def="defaultColDef"
          :row-selection="noSelection"
          :get-row-id="getDiagnosticRowId"
          :get-row-height="getRowHeight"
          :default-row-height="ROW_HEIGHT"
          :max-rows="6"
          :min-grid-height="80"
          :enable-column-menu="true"
        />
      </div>

      <div
        v-if="allocations.length > 0"
        class="bulk-allocation-review-grid"
        aria-label="Bulk Order allocation lines"
      >
        <h3 class="bulk-allocation-review-title">
          Bulk Allocation Lines — Intermediate Orders unavailable
        </h3>
        <p class="bulk-allocation-review-note">
          View only. These rows come from the Bulk Order allocation and have no
          Intermediate PO numbers, so they cannot be used to generate Shipping
          POs or run redistribution.
          <template v-if="showFirstCost && missingPriceCount > 0">
            FirstCost is unavailable for {{ missingPriceCount }} line{{
              missingPriceCount === 1 ? "" : "s"
            }}.
          </template>
        </p>
        <AppGrid
          id="bulk-allocation-lines-grid"
          :column-defs="allocationColumnDefs"
          :row-data="sortedAllocations"
          :default-col-def="defaultColDef"
          :row-selection="noSelection"
          :get-row-id="getAllocationRowId"
          :get-row-height="getRowHeight"
          :default-row-height="ROW_HEIGHT"
          :max-rows="12"
          :enable-column-menu="true"
        />
      </div>
    </div>
  </section>
</template>

<style scoped>
.bulk-allocation-review-body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem 1rem 1rem;
  min-width: 0;
}

.bulk-allocation-review-grid {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
}

.bulk-allocation-review-title {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
}

.bulk-allocation-review-note {
  margin: 0;
  color: var(--dashboard-text-secondary, #5d6878);
  font-size: 0.8rem;
}
</style>
